import { Button } from '@design-system';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { FC, useCallback, useContext, useEffect, useState } from 'react';

import { useOnBfCacheLoad } from '../../../hooks/useOnBfCacheLoad';
import {
  GeneratedCalculateOrderPricingQuery,
  GeneratedPrivateLink,
  GeneratedTaxType,
} from '../../../types/generated';
import { FrameOptionConsolidated } from '../../../types/Products';
import {
  DrawerFormType,
  ProductBasketPurchasingController,
} from '../../../types/Purchasing';
import { useAnalyticsEmitter } from '../../../utils/analytics/emitter';
import {
  shopifyAdminIdToGraphqlId,
  shopifyGraphqlIdToAdminId,
} from '../../../utils/shopifyIds';
import { CrossSellList } from '../../CrossSellList/CrossSellList';
import { CrossSellContext, CrossSellItem } from '../CrossSell/CrossSellContext';
import { BasketReviewItem } from './BasketReviewItem';
import { BasketReviewSummary } from './BasketReviewSummary';
import { ShippingSelector } from './ShippingSelector';
import { Variant, VariantsForm } from './VariantsForm';

const shopifyProductToVariant = (product: Product): Variant => ({
  disabled: product.availability !== 'Available',
  id: product.id,
  label: product.title,
  name: 'variant',
  value: product.id,
});

interface BasketReviewFormProps {
  frameIncluded: boolean;
  frameOptions: FrameOptionConsolidated[] | null;
  formIsVisible: boolean;
  product: ProductType;
  setFormTitle: (formTitle?: string) => void;
  setVisibleForm: (formType: DrawerFormType) => void;
  privateLink?: GeneratedPrivateLink;
  visibleForm: DrawerFormType | undefined;
  basket: ProductBasketPurchasingController<ShopifyProductIdentifier>;
  hasNoOrderCancellation: boolean;
  showTitle?: boolean;
}

export const BasketReviewForm: FC<BasketReviewFormProps> = ({
  basket,
  formIsVisible,
  frameIncluded,
  frameOptions,
  hasNoOrderCancellation,
  privateLink,
  product,
  setFormTitle,
  setVisibleForm,
  showTitle,
  visibleForm,
}) => {
  const { t } = useTranslation('product');
  const [{ id: mainProductId }] = product.id;
  const [loading, setLoading] = useState(false);
  const [variants, setVariants] = useState<Variant[]>([]);
  const { analytics, crossSellItems, crossSellTitle, mainProduct } =
    useContext(CrossSellContext);
  const [analyticsEmitter] = useAnalyticsEmitter(analytics);
  const shouldReduceMotion = useReducedMotion();
  const animationProps = {
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: '-16px' },
    initial: { opacity: 0, x: '16px' },
    transition: { type: 'tween', duration: shouldReduceMotion ? 0 : 0.12 },
  };

  const [calculateOrderPricingData, setCalculateOrderPricingData] = useState<
    GeneratedCalculateOrderPricingQuery['calculateOrderPricing'] | undefined
  >();

  const {
    addProductToBasket,
    calculateBasketPricing,
    checkoutLoading,
    frameBasket,
    goToCheckout,
    productBasket,
    removeProductFromBasket,
    updateProductQuantity,
  } = basket;

  useEffect(() => {
    calculateBasketPricing().then(setCalculateOrderPricingData);
  }, [productBasket, frameBasket, calculateBasketPricing]);

  const shipping = calculateOrderPricingData?.subtotalShippingPrice || 0;
  const subtotal = calculateOrderPricingData?.subtotalItemsPrice || 0;
  const subTotalAfterTaxAndShipping =
    calculateOrderPricingData?.totalPrice || 0;
  const taxCalculation = {
    taxRate: calculateOrderPricingData?.taxRate || 0,
    taxType: calculateOrderPricingData?.taxType || GeneratedTaxType.NoTax,
    calculatedAmount: calculateOrderPricingData?.subtotalTaxPrice || 0,
  };

  const findProductById = useCallback(
    (productId: string) => {
      if (productId === shopifyAdminIdToGraphqlId(mainProductId)) {
        return {
          shopify: mainProduct,
          storyblok: product,
        };
      }
      const variant = crossSellItems.reduce(
        (acc, curr) => {
          if (
            curr.variants.length === 0 &&
            curr.product.shopify?.id === productId
          ) {
            return {
              shopify: curr.product.shopify,
              storyblok: curr.product.storyblok,
            };
          }

          const variantFound = curr.variants.find(
            (v) => v.shopify?.id === productId,
          );
          if (variantFound) return variantFound;

          return acc;
        },
        { shopify: undefined, storyblok: undefined },
      );
      return variant;
    },
    [crossSellItems, mainProduct, mainProductId, product],
  );

  const addVariantToBasket = useCallback(
    (variantId: string) => {
      basket.addProductToBasket({
        productId: { id: variantId },
        quantity: 1,
      });
      analyticsEmitter('cross sell variant', 'added to basket', {
        'variant id': variantId,
        'variant title': variants.find((v) => v.id === variantId)?.label,
      });
    },
    [analyticsEmitter, addProductToBasket, variants],
  );

  const reAddVariantToBasket = useCallback(
    (entryKey: string, quantity: number, variantId: string) => {
      updateProductQuantity(entryKey, quantity);
      analyticsEmitter('cross sell variant', 're-added to basket', {
        'variant id': variantId,
        'variant title': variants.find((v) => v.id === variantId)?.label,
      });
    },
    [analyticsEmitter, updateProductQuantity, variants],
  );

  const handleVariantClick = useCallback(
    ({ variant }: { variant: string }) => {
      const variantId = shopifyGraphqlIdToAdminId(variant);
      const entry = Object.entries(productBasket).find(
        ([, p]) => p.product.productId === variant,
      );
      if (entry) {
        const [entryKey, { product: entryProduct, quantity }] = entry;
        const { shopify } = findProductById(entryProduct.productId);
        if (shopify?.allowMultipleProducts) {
          const atCapLimit =
            shopify?.maximumSalesCapacity !== null &&
            quantity === shopify.maximumSalesCapacity;

          if (!atCapLimit) {
            reAddVariantToBasket(entryKey, quantity + 1, variantId);
          }
        }
      } else {
        addVariantToBasket(variantId);
      }
      setVisibleForm('review');
    },
    [
      addVariantToBasket,
      findProductById,
      productBasket,
      reAddVariantToBasket,
      setVisibleForm,
    ],
  );

  const handleCrossSellItemClick = useCallback(
    (item: CrossSellItem) => {
      analyticsEmitter('cross sell product', 'clicked', {
        'clicked product id': item.product.id,
        'clicked product artist': item.product.artistName,
        'clicked product title': item.product.title,
        'clicked variants count': item.variants.length,
        'clicked variants available': item.variants.filter(
          (v) => v.shopify?.availability === 'Available',
        ).length,
      });

      if (item.variants.length === 0) {
        if (item.product.shopify) {
          handleVariantClick({ variant: item.product.shopify.id });
        }
        return;
      }
      setVariants(
        item.variants.reduce(
          (acc, curr) =>
            curr.shopify
              ? [...acc, shopifyProductToVariant(curr.shopify)]
              : acc,
          [],
        ),
      );
      setVisibleForm('variants');
      setFormTitle(item.variantsTitle);
    },
    [handleVariantClick, analyticsEmitter, setFormTitle, setVisibleForm],
  );

  const handleRemove = useCallback(
    (basketEntryId: string, variant?: Product) => {
      removeProductFromBasket(basketEntryId);
      if (variant) {
        analyticsEmitter('cross sell variant', 'removed from basket', {
          'variant id': shopifyGraphqlIdToAdminId(variant.id),
          'variant title': variant.title,
        });
      }
    },
    [analyticsEmitter, removeProductFromBasket],
  );

  const handleUpdate = useCallback(
    (
      basketEntryId: string,
      prevQuantity: number,
      newQuantity: number,
      variant?: Product,
    ) => {
      updateProductQuantity(basketEntryId, newQuantity);

      if (variant) {
        const variantId = shopifyGraphqlIdToAdminId(variant.id);
        if (variantId !== mainProductId) {
          analyticsEmitter('cross sell variant', 'basket quantity updated', {
            'variant id': variantId,
            'variant title': variant.title,
            'variant quantity': newQuantity,
            'variant prev quantity': prevQuantity,
          });
        }
      }
    },
    [analyticsEmitter, mainProductId, updateProductQuantity],
  );

  const handleCheckout = useCallback(() => {
    setLoading(true);
    const variantsInBasket = Object.values(productBasket).filter(
      (p) => shopifyGraphqlIdToAdminId(p.product.productId) !== mainProductId,
    );
    analyticsEmitter('cross sell checkout', 'submit', {
      'cross sell added': variantsInBasket.length > 0,
      'cross sell basket ids': variantsInBasket.map((v) =>
        shopifyGraphqlIdToAdminId(v.product.productId),
      ),
      'cross sell basket quantities': variantsInBasket.map((v) => v.quantity),
      'cross sell basket titles': variantsInBasket.map(
        (v) => v.product.productTitle,
      ),
    });
    goToCheckout();
  }, [
    analyticsEmitter,
    goToCheckout,
    mainProductId,
    privateLink,
    productBasket,
  ]);

  useOnBfCacheLoad(() => {
    setLoading(false);
  });

  useEffect(() => {
    if (formIsVisible) {
      analyticsEmitter('cross sell review step', 'viewed');
    }
  }, [analyticsEmitter, formIsVisible]);

  useEffect(() => {
    if (visibleForm !== 'variants') {
      setFormTitle(t('drawers.review.checkout.title'));
    }
  }, [setFormTitle, t, visibleForm]);

  return (
    <div className="text-neutral-6">
      <AnimatePresence>
        {visibleForm === 'review' ? (
          <motion.div key="review" {...animationProps}>
            {showTitle && (
              <p className="hidden text-lg font-bold md:block md:pb-8">
                {t('drawers.review.checkout.title')}
              </p>
            )}
            <section className="flex flex-col gap-y-4 md:gap-y-8">
              <div className="flex flex-col gap-y-3">
                {Object.entries(productBasket).map(
                  ([basketEntryId, basketEntry]) => {
                    const { productId } = basketEntry.product;
                    const { shopify, storyblok } = findProductById(productId);
                    if (shopify && storyblok) {
                      const isMainProduct =
                        shopifyGraphqlIdToAdminId(productId) === mainProductId;
                      return (
                        <BasketReviewItem
                          key={basketEntryId}
                          allowMultiples={shopify.allowMultipleProducts}
                          disabled={loading}
                          frame={
                            basketEntry.frameBasketEntryId
                              ? frameBasket[basketEntry.frameBasketEntryId]
                              : undefined
                          }
                          frameIncluded={isMainProduct ? frameIncluded : false}
                          frameOptions={
                            isMainProduct && frameOptions ? frameOptions : []
                          }
                          maximumSalesCapacity={shopify.maximumSalesCapacity}
                          onFrameClick={() => setVisibleForm('frame')}
                          onQuantity={(quantity) => {
                            handleUpdate(
                              basketEntryId,
                              basketEntry.quantity,
                              quantity,
                              shopify,
                            );
                          }}
                          onRemove={
                            isMainProduct
                              ? undefined
                              : () => handleRemove(basketEntryId, shopify)
                          }
                          product={shopify}
                          quantity={basketEntry.quantity}
                        />
                      );
                    }
                    return null;
                  },
                )}
              </div>
              <ShippingSelector
                setVisibleForm={(formType: DrawerFormType) => {
                  if (formType === 'locale') {
                    analyticsEmitter?.('shipping', 'clicked');
                  }
                  setVisibleForm(formType);
                }}
              />
              <BasketReviewSummary
                shipping={shipping}
                subtotal={subtotal}
                subTotalAfterTaxAndShipping={subTotalAfterTaxAndShipping}
                taxAmount={taxCalculation.calculatedAmount}
                taxRate={taxCalculation.taxRate}
                taxType={taxCalculation.taxType}
              />
              <CrossSellList
                items={crossSellItems}
                onSelect={handleCrossSellItemClick}
                title={crossSellTitle}
              />
              <div className="pb-12" data-testid="review-form-checkout-cta">
                <Button
                  disabled={loading}
                  fullWidth
                  label={t('drawers.review.checkout.cta')}
                  loading={loading || checkoutLoading}
                  onClick={handleCheckout}
                  variant="primary"
                />

                {hasNoOrderCancellation && (
                  <div className="pt-3">
                    <p className="text-center text-xs text-neutral-4">
                      {t('drawers.review.checkout.note')}
                    </p>
                  </div>
                )}
              </div>
            </section>
          </motion.div>
        ) : null}

        {visibleForm === 'variants' ? (
          <motion.div key="variants" {...animationProps}>
            {Boolean(variants.length) && (
              <VariantsForm onSubmit={handleVariantClick} variants={variants} />
            )}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};
